import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that adds a single merchant-picked "essential" product to
 * the cart from the cart drawer's upsell card. See snippets/cart-essential-upsell.liquid.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} addButton - The add button.
 *
 * @extends {Component<Refs>}
 */
class CartEssentialUpsell extends Component {
  requiredRefs = ['addButton'];

  /**
   * Adds the essential product (quantity 1) to the cart and refreshes every
   * cart-items-component on the page, mirroring product-form.js's add flow
   * so the drawer contents and header cart count both stay correct.
   * @param {MouseEvent} event
   */
  handleAdd = async (event) => {
    event.preventDefault();

    const { addButton } = this.refs;
    if (addButton.disabled) return;

    const variantId = this.dataset.variantId;
    const productId = this.dataset.productId;
    if (!variantId) return;

    addButton.disabled = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sectionIds.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (data.status) {
        // Failed (e.g. went out of stock between page load and this click).
        // Nothing more useful to show on a small upsell card than just
        // re-enabling the button.
        addButton.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'cart-essential-upsell', {
          // This really is a product-add action, just triggered from a
          // simplified upsell card instead of a full product form -- using
          // the same source string is what makes cart-icon.js add 1 to the
          // existing bubble count instead of treating itemCount as the
          // cart's new absolute total.
          source: 'product-form-component',
          itemCount: 1,
          productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      addButton.disabled = false;
    }
  };
}

if (!customElements.get('cart-essential-upsell-component')) {
  customElements.define('cart-essential-upsell-component', CartEssentialUpsell);
}
